import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import {
  completeFormSession,
  updateFormSessionProgress,
} from "@/utils/session";
import { WIZARD_STEPS } from "@/constants";
import {
  deleteEntityData,
  saveEntityData,
  saveEvaluation,
  saveServiceSelections,
  saveStepProgress,
} from "@/utils/database";
import { EntityData } from "../types";
import { queryKeys } from "../lib/queryClient";
import { useQueryClient } from "@tanstack/react-query";

export function useWizardSession() {
  const [operationInProgress, setOperationInProgress] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  // Use SessionSwitcher context for session management
  const {
    currentSessionId: sessionId,
    sessionData,
    isLoadingSessionSwitch: isLoading,
    refreshCurrentSession,
  } = useSessionSwitcher();

  // Comprehensive logging utility
  const logSessionState = (operation: string, details?: any) => {
    console.log(`🔄 SESSION[${operation}]:`, {
      sessionId,
      isLoading,
      operationInProgress,
      userId: user?.id,
      timestamp: new Date().toISOString(),
      ...details,
    });
  };

  const updateProgress = async (
    currentStep: number,
    currentSubstep: number
  ) => {
    if (!sessionId) return false;

    try {
      const success = await updateFormSessionProgress(
        sessionId,
        currentStep,
        currentSubstep
      );
      if (success) {
        await saveStepProgress(
          sessionId,
          currentStep,
          getStepName(currentStep),
          currentSubstep
        );

        // Refresh the session switcher to show updated progress
        await refreshCurrentSession();
      }
      return success;
    } catch (error) {
      console.error("Error updating progress:", error);
      return false;
    }
  };

  const saveEntity = async (entityData: EntityData) => {
    if (!sessionId) return false;

    try {
      await saveEntityData(sessionId, entityData);
      queryClient.invalidateQueries({
        queryKey: queryKeys.entityData.bySession(sessionId),
      });
    } catch (error) {
      console.error("Error saving entity data:", error);
      return false;
    }
  };

  const deleteCurrentEntityData = async () => {
    if (!sessionId) return false;
    try {
      await deleteEntityData(sessionId);
      await refreshCurrentSession();
      return true;
    } catch (error) {
      console.error("Error deleting entity data:", error);
      return false;
    }
  };

  const saveServices = async (
    selectedServices: string[],
    allServices: any[]
  ) => {
    if (!sessionId) return false;

    try {
      return await saveServiceSelections(
        sessionId,
        selectedServices,
        allServices
      );
    } catch (error) {
      console.error("Error saving service selections:", error);
      return false;
    }
  };

  const saveEvaluationResult = async (
    evaluationType: string,
    result: string,
    criteria: any,
    requiresImpactAnalysis = false,
    impactAnalysisResult?: string
  ) => {
    if (!sessionId) return false;

    try {
      return await saveEvaluation(
        sessionId,
        evaluationType,
        result,
        criteria,
        requiresImpactAnalysis,
        impactAnalysisResult
      );
    } catch (error) {
      console.error("Error saving evaluation:", error);
      return false;
    }
  };

  const completeSession = async () => {
    if (!sessionId) return false;

    try {
      const success = await completeFormSession(sessionId);
      if (success) {
        // Refresh the session switcher to show completion status
        await refreshCurrentSession();
      }
      return success;
    } catch (error) {
      console.error("Error completing session:", error);
      return false;
    }
  };

  const getStepName = (stepNumber: number): string => {
    return WIZARD_STEPS[stepNumber] || "Pas necunoscut";
  };

  return {
    sessionId,
    isLoading,
    sessionData,
    updateProgress,
    saveEntity,
    saveServices,
    saveEvaluationResult,
    completeSession,
    deleteCurrentEntityData,
  };
}
